// Please do not remove next line. Compulsory for declaring global var `google` with ts type
/// <reference types="@types/googlemaps" />

import { Component, Inject, NgZone, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { GtmService } from '@app/core/services/analytics/gtm.service';
import { LocationService } from '@core/services/location/location.service';
import { ToastrService } from '@app/core/services/notification/toastr.service';
import { GoogleMapService } from '@core/services/google_map_service/google-map.service';
import { LocationApiService } from '@core/services/google_map_service/location-api.service';

import { BaseComponent } from '@core/components/base/base.component';
import { AddAddressComponent } from '@app/shared/components/add-address/add-address.component';

import { LatLng, UserLocation } from '@app/shared/models/util';
import { ReverseGeoCodeResponse } from '@app/shared/models/api-response/reverse-geocode-response';
import { SelectorComponent } from '../../../shared/components/dummy-template/selector/selector.component';
import { GooglePlaceDirective } from '../../../shared/directives/google-place.directive';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { AddressService } from '@app/core/services/address/address.service';
import { PolygonInfo, PolygonService } from '@app/core/services/polygon/polygon.service';

type Modes = 'edit-address' | 'add-new-address' | 'profile-add-new-address' | 'outlet-selection-screen';

@Component({
    selector: 'app-google-map',
    templateUrl: './google-map.component.html',
    styleUrls: ['./google-map.component.scss'],
    standalone: true,
    imports: [MatIconModule, FormsModule, GooglePlaceDirective, SelectorComponent]
})
export class GoogleMapComponent extends BaseComponent {

	@ViewChild('map', { static: true }) mapElement: ElementRef;

    mode: Modes;
    currentLocation: UserLocation;

    googleMap: google.maps.Map;
    mapOptions: google.maps.MapOptions;

    isHeader = false;
    isDragging = false;
    findingLocality = false;

    addressLineHash: {
        [key: string]: string | null;
    } = {};
    addressInputForm: any;
    addressFormName: string;

	googleInputsearchText: string;
    selectedPolygon: any = null;
	poiPolygons: any = [];

    dragStartEventListener: google.maps.MapsEventListener;
    dragEndEventListener: google.maps.MapsEventListener;
    zoomEventListener: google.maps.MapsEventListener;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private ngZone: NgZone,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private gtmService: GtmService,
        private bottomSheet: MatBottomSheet,
        private toastrService: ToastrService,
        private gMapService: GoogleMapService,
        private locationService: LocationService,
        private locationApiService: LocationApiService,
        private addressService: AddressService,
        private dialogRef: MatDialogRef<GoogleMapComponent>,
        private polygonService: PolygonService
    ) {
        super();
    }

    ngOnInitBrowser() {

		const { formatted_address, latitude, longitude, mode, referer } = this.data;

        this.mode = mode;
        this.isHeader = Boolean(referer === 'header');

        const lat = Number(latitude);
        const lng = Number(longitude);

        this.currentLocation = {
            latitude: lat,
            longitude: lng,
            formatted_address
        };

        this.mapOptions = {
            zoom: 19,
            zoomControl: false,
            clickableIcons: false,
            center: { lat, lng },
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            styles: this.gMapService.getMapStyles(),
            minZoom: 16,
            maxZoom: 20
        } as google.maps.MapOptions;

        this.gtmService.logEvent('web_analytics', {
            event_action: 'address',
            event_label: 'Google Map Open',
            value1: 'Mode: ' + mode
        });
    }

    ngAfterViewInit() {

        this.ngZone.runOutsideAngular(() => {
			this.gMapService
				.loadGoogleMap(this.afterGoogleMapLoadTasks.bind(this))
					.catch(() => {
						this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
					});
        });
    }

    afterGoogleMapLoadTasks() {
        this.initializeGoogleMap();
        // pan to input latLng location
        this.panToLocation();
    }

    locateMe() {
        this.findingLocality = true;
        const startTime = Date.now();
        this.googleMap.setOptions({ draggable: false });

        this.locationService
            .fetchLocation()
            .then((latlng: LatLng) => {
                this.googleMap.panTo(latlng);
                this.reverseGeocode(latlng);
            })
            .catch(error => {
                const { message, code } = error;
                if (message) {
                    this.toastrService.openSnackBar(message, 'OKAY');
                }

                if (code) {
                    this.gtmService.logWebAnalyticsEvent('address', 'Location fetch failed with code: ' + code);
                }
                this.findingLocality = false;
            })
            .finally(() => {
                this.googleMap.setOptions({ draggable: true });
                this.gtmService.endTimer(startTime, 'web_analytics', {
                    event_action: 'address',
                    event_label: 'Completion Time',
                });
            });
    }

    private panToLocation() {
        this.findingLocality = true;
        const { latitude, longitude } = this.currentLocation;
        this.googleMap.panTo({
            lat: latitude,
            lng: longitude
        });
        this.reverseGeocode({ lat: latitude, lng: longitude });
    }

    private initializeGoogleMap() {
        this.googleMap = new google.maps.Map(this.mapElement.nativeElement as HTMLElement, this.mapOptions);

        // Drag Start Event Listener
        this.dragStartEventListener = this.googleMap.addListener('dragstart', () => {
            this.isDragging = true;
            this.cdr.detectChanges();
        });

        // Drag Stop Event Listener
        this.dragEndEventListener = this.googleMap.addListener('dragend', () => {
            this.isDragging = false;
            this.findingLocality = true;
            this.gtmService.logEvent('web_analytics', {
                event_action: 'address',
                event_label: 'Map Dragged',
                value1: 'Mode: ' + this.mode
            });
            const latlng = this.googleMap.getCenter().toJSON();
            this.reverseGeocode(latlng);
            this.cdr.detectChanges();
        });
        // Zoom Event Listener
        this.zoomEventListener = this.googleMap.addListener('zoom_changed', async () => {
            const { latitude, longitude } = this.currentLocation;
            this.gtmService.logEvent('web_analytics', {
                event_action: 'address',
                event_label: 'Map Zoomed',
                value1: 'Mode: ' + this.mode,
                value2: 'Zoom Level: ' + this.googleMap.getZoom()
            });

            this.googleMap.panTo({ lat: latitude, lng: longitude });
            this.cdr.detectChanges();
        });
    }

    async updatePolygon(lat: number, lng: number) {

        const polygons: any = await this.polygonService.getPolygons(lat, lng);

        if (polygons !== null) {
            // clear polygon which doesn't exists in new response
            const old_polygon_ids = new Set(this.poiPolygons.map(p => p.getId()));
            const new_polygon_ids = new Set(polygons.map(p => p.polygon_id));

            this.poiPolygons = this.poiPolygons
				.filter((curr_poi) => {
					if (!new_polygon_ids.has(curr_poi.getId())) {
						curr_poi.getPolygon().setMap(null);
						curr_poi.getInfoWindow().close();
						return false;
					}
					return true;
				});

            polygons.forEach(polygon => {
                if (!old_polygon_ids.has(polygon.polygon_id)) {
                    const new_poi = new PolygonInfo(polygon);
                    new_poi.getPolygon().setMap(this.googleMap)
                    new_poi.getInfoWindow().open(this.googleMap);
                    this.poiPolygons.push(new_poi);
                }
            });
        }

		this.selectedPolygon = null;

		this.poiPolygons.sort((a, b) => {
			const a_center = a.getPolygon().map.getCenter();
			const b_center= b.getPolygon().map.getCenter();
			return a_center.lat() - b_center.lat();
		});

		this.poiPolygons.forEach((curr_poi, index) => {
			const is_inside = google.maps.geometry.poly.containsLocation(new google.maps.LatLng(lat, lng), curr_poi.getPaths());
			if (is_inside) {
				this.selectedPolygon = curr_poi;
			}
			curr_poi.highlight(is_inside);
			const prev = index === 0 ? null : this.poiPolygons[index - 1];
			curr_poi.updateInfoWindowPosition(lat, prev);
		});

		this.selectedPolygon?.getInfoWindow()?.setOptions({
			content: `<div class="poi-name selected"> ${this.selectedPolygon.getName()} <div>`,
			position: {
				lat: lat,
				lng: lng
			}
		});

        this.cdr.detectChanges();
    }

    async reverseGeocode(latlng: LatLng) {
        return this.locationApiService
            .reverseGeocode({ ...latlng })
            .then(async (res: ReverseGeoCodeResponse) => {
                if (res && res.meta.code === 200 && res.meta.status === google.maps.GeocoderStatus.OK) {
                    const { geocode_info, address_input_form, address_form_name } = res.data;
                    this.addressFormName = address_form_name;
                    this.addressInputForm = address_input_form;
                    await this.updatePolygon(latlng.lat, latlng.lng);
                    this._updataLocationDetails(latlng, geocode_info);
                    this.gtmService.logEvent('web_analytics', {
                        event_action: 'address',
                        event_label: 'Reverse Geocode response received from get_reverse_geocode API',
                        value1: 'Mode: ' + this.mode
                    });
                } else {
                    return this.reverseGeocodeUsingGoogleService(latlng);
                }
            })
            .catch(() => {
                return this.reverseGeocodeUsingGoogleService(latlng);
            });
    }

    reverseGeocodeUsingGoogleService(latlng: LatLng) {
        const _this = this;
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {
                location: latlng
            },
            function (results, status) {
                if (status === google.maps.GeocoderStatus.OK) {
                    _this._updataLocationDetails(latlng, results);
                    _this.gtmService.logEvent('web_analytics', {
                        event_action: 'address',
                        event_label: 'Reverse Geocode response received from geocoder',
                        value1: 'Mode: ' + _this.mode
                    });
                }
            }
        );
    }

    private _updataLocationDetails(latlng: LatLng, results: any) {
        const details = results[0];
        if (details) {
            const { lat, lng } = latlng;
			const heading_text = this.selectedPolygon?.getName() || details.heading_text;
            const { formatted_address } = details;
            this._selectLocation({
                heading_text,
                latitude: lat,
                longitude: lng,
                formatted_address
            });

            this.findingLocality = false;
            this.cdr.detectChanges();
        }
    }

    private _selectLocation(locationDetails: UserLocation) {
        this.currentLocation = locationDetails;
    }

    async afterSelection(place: any) {
        const { heading_text, formatted_address } = place;
        const { lat, lng } = this.addressService.getLatLng(place);
        this.googleMap.setCenter({ lat, lng });

        const selectedLocation = {
            heading_text,
            formatted_address,
            latitude: this.googleMap.getCenter().lat(),
            longitude: this.googleMap.getCenter().lng()
        };

        await this.updatePolygon(lat, lng);
        this._selectLocation(selectedLocation);
        this.cdr.detectChanges();
    }

    selectLocality() {
        if (!this.isHeader) {
            this.createAddress(this.currentLocation);
        } else {
            this.dialogRef.close(this.currentLocation);
        }
    }

    createAddress(mapData: UserLocation) {
        const isEditable = this.mode === 'edit-address';
        const address_input_form = this.addressInputForm;
        const address_form_name = this.addressFormName;

        const data = {
            isEditable,
            ...this.data,
            ...mapData,
            address_form_name,
            address_input_form,
			polygon: this.selectedPolygon
        };

        // this case can come when, `get_reverse_geocode` api fails, and we have info from geocoder api
        if (!address_input_form) {
            this.gtmService.logEvent('web_analytics', {
                event_action: 'address',
                event_label: 'Address Form Not Present',
                value1: 'Mode: ' + this.mode
            });
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            this.closeDialog();
            return;
        }

        if (this.isMobile) {
            const dialogRef = this.bottomSheet.open(AddAddressComponent, {
                data,
                panelClass: 'add-address-bottom-sheet'
            });

            dialogRef.afterDismissed().subscribe(data => {
                this.afterCloseHandler(data);
            });
        } else {
            const dialogRef = this.dialog.open(AddAddressComponent, {
                data,
                panelClass: 'add-address-dialog'
            });

            dialogRef.afterClosed().subscribe(data => {
                this.afterCloseHandler(data);
            });
        }
    }

    afterCloseHandler(data: any) {
        if (data) {
            this.gtmService.logEvent('web_analytics', {
                event_action: 'address',
                event_label: 'Address Form Filled',
                value1: 'Mode: ' + this.mode
            });
            this.dialogRef.close(data);
        } else {
            this.gtmService.logEvent('web_analytics', {
                event_action: 'address',
                event_label: 'Address Form Not Filled',
                value1: 'Mode: ' + this.mode
            });
        }
    }

    clearSearchText() {
        this.googleInputsearchText = '';
    }

    closeDialog() {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.dragStartEventListener?.remove();
        this.dragEndEventListener?.remove();
        this.zoomEventListener?.remove();
    }
}
